/**
 * The five/six-stage waits this product has — reading a workbook, processing
 * a file — drawn once, not once per screen. `IngestProgress.tsx` and
 * `FileProcessing.tsx` used to each carry their own copy of the same
 * `row spread up-item` + status-badge markup; this is that markup's MUI
 * replacement, factored out so the two screens stay visually identical the
 * way their own comments already say they should.
 *
 * Horizontal, not stacked — five rows of badge+label+detail took five times
 * the height a `Stepper` needs for the same information, for no benefit: the
 * reader is watching ONE thing move, not reading five as prose.
 */
import {
	Box,
	CircularProgress,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import type { StageProgressProps, StageStatus } from './types';

function StageIcon({ status }: { status: StageStatus }) {
	if (status === 'FAILED')
		return <ErrorIcon color="error" fontSize="small" />;
	if (status === 'DONE')
		return <CheckCircleIcon color="success" fontSize="small" />;
	if (status === 'RUNNING')
		return <CircularProgress size={18} thickness={5} />;
	return (
		<Box
			className="step-dot"
			sx={{
				width: 18,
				height: 18,
				borderRadius: '50%',
				border: '2px solid var(--line-strong)',
			}}
		/>
	);
}

export default function StageProgress({
	stages,
	statusOf,
	detailOf,
}: StageProgressProps) {
	return (
		<Stepper alternativeLabel sx={{ width: '100%' }}>
			{stages.map((stage) => {
				const status = statusOf(stage.key);
				const detail = detailOf?.(stage.key);
				return (
					<Step key={stage.key} completed={status === 'DONE'}>
						<StepLabel
							error={status === 'FAILED'}
							icon={<StageIcon status={status} />}
							optional={
								detail ? (
									<Typography
										variant="caption"
										className="mut"
										component="span"
									>
										{detail}
									</Typography>
								) : undefined
							}
						>
							<Typography
								variant="body2"
								className={status === 'PENDING' ? 'mut' : 'b'}
							>
								{stage.label}
							</Typography>
						</StepLabel>
					</Step>
				);
			})}
		</Stepper>
	);
}
